import { useEffect, useRef, useState } from 'react';
import { useStableCallback } from '../../shared/use_stable_callback.js';
import type { DayTone } from '../../shared/travel_calendar.js';
import type { PlannerHeatView, PlannerWindow } from './heat_view.js';
import type { PlannerLeg } from './planner_models.js';
import { routeHeatKey } from './leg_state.js';
import { dayToneOf, tierDayState, tiersTone } from './heat_tone.js';

/*
 * O CALOR da folha de datas do celular (#3446): a coluna de meses é do
 * `TravelCalendar` (nasce em hoje e cresce ao rolar); o que é do planner é
 * lembrar as janelas de heat que já chegaram para a rota e pedir a próxima
 * quando a coluna anuncia meses ainda sem janela (`onMonthsChange`).
 *
 * No popover a grade mostra a janela corrente do heat (2 de até 3 meses) e a
 * seta TROCA a janela — o host substitui a vista. Numa coluna rolável trocar a
 * janela apagaria os meses que a pessoa acabou de ver; aqui o kit guarda as
 * janelas que já chegaram. Escolha, piso e pedido de heat seguem os do planner.
 */

/** Desloca um 'YYYY-MM' por `n` meses (UTC). Usado no re-âncora do pager: o
 *  próximo/anterior chunk da janela pede um novo heatmap re-ancorado. */
export function addMonths(ym: string, n: number): string {
  const [y, m] = ym.split('-').map(Number);
  if (y === undefined || m === undefined) throw new Error('Expected a YYYY-MM month.');
  return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 7);
}

/** `count` meses 'YYYY-MM' a partir de `fromMonth`, inclusive. */
function monthsFrom(fromMonth: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => addMonths(fromMonth, i));
}

/** Meses 'YYYY-MM' consecutivos da janela pré-buscada. */
export function windowMonths(window: PlannerWindow): string[] {
  return monthsFrom(window.fromMonth, window.count);
}

type Archive = {
  key: string;
  outTiers: Map<string, number>;
  retTiers: Map<string, number>;
  /** Os meses cuja janela já chegou — os demais estão por pedir. */
  months: Set<string>;
};

function emptyArchive(key: string): Archive {
  return { key, outTiers: new Map(), retTiers: new Map(), months: new Set() };
}

/** `archive` with what `heat` adds to it; the SAME archive when it adds nothing
 *  (the merge is idempotent: same heat, same result). The check reads the tier
 *  the merge keeps (the last one per date), so a heat that repeats a date still
 *  converges instead of re-rendering forever. */
function absorbHeat(archive: Archive, heat: PlannerHeatView): Archive {
  const [origin, destination] = archive.key.split('|');
  if (heat.route.origin !== origin || heat.route.destination !== destination) return archive;
  const months = monthsFrom(heat.window.fromMonth, heat.window.count);
  const outTiers = new Map((heat.outbound?.days ?? []).map((day) => [day.date, day.tier]));
  const retTiers = new Map((heat.return?.days ?? []).map((day) => [day.date, day.tier]));
  const adds =
    months.some((month) => !archive.months.has(month)) ||
    [...outTiers].some(([date, tier]) => archive.outTiers.get(date) !== tier) ||
    [...retTiers].some(([date, tier]) => archive.retTiers.get(date) !== tier);
  if (!adds) return archive;
  return {
    key: archive.key,
    outTiers: new Map([...archive.outTiers, ...outTiers]),
    retTiers: new Map([...archive.retTiers, ...retTiers]),
    months: new Set([...archive.months, ...months]),
  };
}

/**
 * As janelas de heat da forma BINÁRIA acumuladas, enquanto descrevem o mesmo
 * contexto: a rota e a fase da escolha (`contextKey`). O host substitui a vista
 * a cada leitura, então quem lembra das anteriores é este arquivo. Contexto
 * novo (outra rota, ou a ida escolhida — o heat passa a trazer a volta)
 * descarta tudo: as cores anteriores descrevem outra pergunta.
 *
 * Mescla no render (estado ajustado no render, não um efeito): num efeito o
 * primeiro render da janela nova sairia cinza.
 */
function useHeatArchive(heat: PlannerHeatView, contextKey: string): Archive {
  const [archive, setArchive] = useState(() => absorbHeat(emptyArchive(contextKey), heat));
  const current = absorbHeat(archive.key === contextKey ? archive : emptyArchive(contextKey), heat);
  if (current !== archive) setArchive(current);
  return current;
}

/** O que a folha entrega ao `TravelCalendar`: a cor de cada dia e o pedido de
 *  calor pelos meses em cena. */
type SheetHeat = {
  readonly dayTone: (date: string) => DayTone | undefined;
  /** Pede o heat do primeiro mês EM CENA sem janela — a folha abre no mês de
   *  hoje e a janela do host começa no mês da ida, então os meses entre os dois
   *  nasceriam cinza sem ninguém pedi-los (o popover os alcançava pela seta).
   *  Chamado pelo calendário a cada mudança da coluna (abertura e crescimento). */
  readonly onMonthsChange: (months: readonly string[]) => void;
};

/**
 * O pedido de heat pelos meses em cena: o primeiro ainda sem janela — só com
 * rota e sem leitura em voo (uma janela por vez; a próxima sai na próxima
 * mudança da coluna, na próxima abertura, ou assim que a leitura em voo
 * terminar, se o pedido a encontrou no caminho).
 */
function useHeatOnDemand({
  covered,
  canFetch,
  fetchFrom,
}: {
  readonly covered: ReadonlySet<string>;
  readonly canFetch: boolean;
  readonly fetchFrom: (month: string) => void;
}): (months: readonly string[]) => void {
  // Um pedido que chegou com uma leitura em voo não se perde: fica devendo e
  // sai quando o host volta a aceitar (a folha aberta durante um heat do
  // popover nascia cinza até a pessoa rolar).
  const owed = useRef<readonly string[] | null>(null);
  // Stable: the calendar announces through it, and the effect below retries it.
  const askMissing = useStableCallback((months: readonly string[]) => {
    if (!canFetch) {
      owed.current = months;
      return;
    }
    owed.current = null;
    const missing = months.find((month) => !covered.has(month));
    if (missing) fetchFrom(missing);
  });
  useEffect(() => {
    if (canFetch && owed.current) askMissing(owed.current);
  }, [canFetch, askMissing]);
  return askMissing;
}

/**
 * A folha da forma BINÁRIA: o calor de todas as janelas já lidas para a rota
 * nesta fase da escolha, e o pedido do que a coluna mostra sem janela. Fora
 * do `useBinaryPlanner` pelo teto de linhas dele; `preview` é o mesmo read do
 * pager (com as datas explícitas da fase da volta).
 */
export function useBinarySheet({
  heat,
  loading,
  origin,
  destination,
  out,
  ret,
  pickingReturn,
  canFetch,
  preview,
}: {
  readonly heat: PlannerHeatView;
  readonly loading: boolean;
  readonly origin: string;
  readonly destination: string;
  readonly out: string | undefined;
  readonly ret: string | undefined;
  readonly pickingReturn: boolean;
  readonly canFetch: boolean;
  readonly preview: (origin: string, destination: string, fromMonth: string) => void;
}): SheetHeat {
  // O contexto do arquivo é a rota mais a fase da volta: a ida escolhida troca
  // a pergunta (o heat passa a trazer a volta) e as cores antigas saem.
  const archive = useHeatArchive(
    heat,
    `${origin}|${destination}|${pickingReturn && out ? out : ''}`,
  );
  const onMonthsChange = useHeatOnDemand({
    covered: archive.months,
    canFetch,
    fetchFrom: (month) => {
      preview(origin, destination, month);
    },
  });
  return {
    // Na folha a espera é por mês: os já lidos seguem pintados e só o que
    // ainda não tem janela shimmera.
    dayTone: (date) =>
      dayToneOf(
        tierDayState(date, {
          out,
          ret,
          pickingReturn,
          outTiers: archive.outTiers,
          retTiers: archive.retTiers,
        }),
        loading && !archive.months.has(date.slice(0, 7)),
      ),
    onMonthsChange,
  };
}

/** Os tiers de UMA rota do multi, unidos por todas as janelas que o host já
 *  mesclou em `routeHeat` (a chave é rota + mês de início da janela). */
function legTiersAcross(
  heat: PlannerHeatView,
  origin: string,
  destination: string,
): { readonly tiers: Map<string, number>; readonly months: Set<string> } {
  const tiers = new Map<string, number>();
  const months = new Set<string>();
  const prefix = routeHeatKey(origin, destination, '');
  for (const [key, leg] of Object.entries(heat.routeHeat ?? {})) {
    if (!key.startsWith(prefix)) continue;
    // A cobertura é a JANELA respondida, não os dias com preço: uma resposta
    // vazia (rota sem tarifa no mês) deixava o mês descoberto e a folha o
    // pedia de novo a cada mudança da coluna, sem nunca avançar ao seguinte.
    // O que a chave guarda é o mês de início da janela — é o mínimo que a
    // resposta cobre.
    months.add(key.slice(prefix.length));
    for (const day of leg.days) {
      tiers.set(day.date, day.tier);
      months.add(day.date.slice(0, 7));
    }
  }
  return { tiers, months };
}

/**
 * A folha do MULTI: o calor de todas as janelas que o host já mesclou para a
 * rota da perna (`routeHeat`). Pedir mais é re-ancorar o pager da perna no
 * primeiro mês sem janela — o `ask` do `useMultiPlanner` vê que ela não cobre
 * e busca.
 */
export function useMultiSheet({
  heat,
  leg,
  loading,
  canFetch,
  rewindow,
}: {
  readonly heat: PlannerHeatView;
  readonly leg: PlannerLeg | undefined;
  readonly loading: boolean;
  readonly canFetch: boolean;
  /** Re-ancora o pager da perna no mês pedido (quem trava no piso é o planner). */
  readonly rewindow: (month: string) => void;
}): SheetHeat {
  const across = legTiersAcross(heat, leg?.origin ?? '', leg?.destination ?? '');
  const onMonthsChange = useHeatOnDemand({
    covered: across.months,
    canFetch: canFetch && leg != null,
    fetchFrom: rewindow,
  });
  return { dayTone: tiersTone(across.tiers, loading && across.tiers.size === 0), onMonthsChange };
}
